import "./DocumentPreview.css";
import React, {
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext,
  ReactNode,
  forwardRef,
} from "react";
import { useContract } from "../ContractContext";
import { ZoomableScrollImage } from "./ZoomableImage";
import { Cancelable, makeCancelable } from "../../utils";
import { LoadingText } from "../../shared/LoadingText";
import { useLoadOncePerLocation } from "../../shared/useLoadOncePerLocation";
import { useContractClient } from "../../api/ContractClient";
import { ApiError } from "../../api/ApiClient";
import { DateString, Role } from "../model/ContractViewModel";

export interface PublicDocumentPreviewViewModel {
  hash: string;
  previewUrl: string;
  expires: DateString;
}

interface DocumentPreviewClient {
  loading: boolean;
  result: PublicDocumentPreviewViewModel | null;
  error: ApiError | null;
  load(): void;
}

function useDocumentPreviewClient(): DocumentPreviewClient {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<PublicDocumentPreviewViewModel | null>(
    null
  );
  const contract = useContract();
  const client = useContractClient();
  const { id } = contract;

  const { load, loadedLocation } = useLoadOncePerLocation();

  useEffect(() => {
    if (!loadedLocation) return;

    setLoading(true);
    setResult(null);

    const cancelablePromise = makeCancelable(
      client.get<PublicDocumentPreviewViewModel>(
        `contract/${id}/document/preview`
      )
    );

    cancelablePromise.promise
      .then((document) => {
        setResult(document);
        setLoading(false);
      })
      .catch((error) => {
        console.log("get document preview error", error);
        if (!error.isCanceled) {
          setLoading(false);
          setError(error);
        }
      });

    return () => cancelablePromise.cancel();
  }, [id, client, contract.documentHash, loadedLocation]);

  return useMemo(
    () => ({
      loading,
      result,
      error,
      load,
    }),
    [loading, result, error, load]
  );
}

const DocumentPreviewContext = createContext<DocumentPreviewClient | null>(
  null
);

export function DocumentPreviewProvider({ children }: { children: ReactNode }) {
  const context = useDocumentPreviewClient();

  return (
    <DocumentPreviewContext.Provider value={context}>
      {children}
    </DocumentPreviewContext.Provider>
  );
}

export function useDocumentPreview() {
  const context = useContext(DocumentPreviewContext);
  const [loading, setLoading] = useState(true);

  if (!context) throw new Error("No DocumentPreviewContext found!");

  const { load } = context;

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setLoading(context.loading);
  }, [context.loading]);

  return { ...context, loading: loading || context.loading };
}

interface CanDownloadDocumentResponse {
  canDownload: boolean;
  hasAdditionalProduct: boolean;
  role?: Role;
}

export function useCanDownloadDocument({
  filename,
  runCheck,
}: { filename?: string; runCheck?: boolean } = {}) {
  const contract = useContract();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<CanDownloadDocumentResponse | null>(
    null
  );
  const client = useContractClient();
  const { id, signature } = contract;
  const signatureStatus = signature ? signature.signatureStatus : null;
  const signedDocumentReady = signature ? signature.signedDocumentReady : null;
  const isSigned = signatureStatus === 1;

  const url =
    `contract/${id}/document/canDownload` +
    (filename ? `?filename=${filename}` : "");

  useEffect(() => {
    async function executeCancelable(
      cancelable: Cancelable<CanDownloadDocumentResponse>
    ) {
      try {
        const document = await cancelable.promise;
        setResult(document);
        setLoading(false);
      } catch (error) {
        if (!error.isCanceled) {
          setLoading(false);
          setError(error);
        }
      }
    }

    setLoading(true);
    setResult(null);

    if (runCheck) {
      const cancelable = makeCancelable(
        client.get<CanDownloadDocumentResponse>(url)
      );

      executeCancelable(cancelable);

      return () => cancelable.cancel();
    }
  }, [client, isSigned, signedDocumentReady, url, runCheck]);

  return useMemo(
    () => ({
      result,
      loading,
      error,
    }),
    [loading, result, error]
  );
}

export interface DocumentPreviewProps {
  loading: boolean;
  result?: PublicDocumentPreviewViewModel;
}

const DocumentPreview = forwardRef<HTMLDivElement, DocumentPreviewProps>(
  function ({ loading, result }, ref) {
    const [imageDimensions, setImageDimesions] = useState<{
      height: number;
      width: number;
    } | null>(null);

    return (
      <div ref={ref}>
        <div
          className="DocumentPreview-container"
          style={{
            paddingTop: imageDimensions
              ? `${(imageDimensions.height / imageDimensions.width) * 100}%`
              : `${Math.sqrt(2) * 100}%`,
          }}
        >
          {loading ? (
            <div className="DocumentPreview-placeholder">
              <LoadingText text="Henter forhåndsvisning" />
            </div>
          ) : document && result ? (
            <ZoomableScrollImage
              onLoad={(
                event: React.SyntheticEvent<HTMLImageElement, Event>
              ) => {
                const { height, width } = (
                  event.target as HTMLImageElement
                ).getBoundingClientRect();

                setImageDimesions({ height, width });
              }}
              className="DocumentPreview"
              alt=""
              src={result.previewUrl}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

export default DocumentPreview;
